/**
 * Import Monitor — Order Lookup tab.
 *
 * Search an order number and see the record exactly as the ERP sent it,
 * whatever the import outcome was. The Debug tab only shows payloads for
 * failed and validation-skipped rows (they're the only ones that get an
 * import-log entry), so a cleanly-imported order was previously impossible
 * to inspect — which is the case you actually need when a customer says
 * "the portal isn't showing my change".
 *
 * Payloads are retained for the last couple of pushes per site only, so this
 * answers "what did we just receive for this order", not "what did we receive
 * last month".
 */
import { useCallback, useState } from 'react';
import { Loader2, Search, AlertTriangle, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { importsAPI, type RawRecordLookup, type RawRecordMatch } from '../../lib/api/imports';

/** Fields people actually chase, surfaced before the full payload. */
const HIGHLIGHT: { key: string; label: string }[] = [
  { key: 'Line_No', label: 'Line' },
  { key: 'Supplier_Promise_Date', label: 'Promise date' },
  { key: 'Due_Date', label: 'Due date' },
  { key: 'Status', label: 'Status' },
  { key: 'Qty_Ordered', label: 'Qty ordered' },
  { key: 'Qty_Received', label: 'Qty received' },
  { key: 'OnHold', label: 'On hold' },
];

const OUTCOME_STYLES: Record<string, string> = {
  created: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  updated: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  unchanged: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  skipped: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

function fmt(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

interface Props {
  /** Site currently selected in the header, so results match what's on screen. */
  siteId?: number | null;
}

export default function ImportOrderLookupTab({ siteId }: Props) {
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RawRecordLookup | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const search = useCallback(async () => {
    const order = term.trim();
    if (!order) return;
    setLoading(true);
    setResult(null);
    setExpanded(null);
    try {
      const data = await importsAPI.getRawRecord({
        order_no: order,
        ...(siteId ? { site: siteId } : {}),
      });
      setResult(data);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }, [term, siteId]);

  const dupKeys = new Set(
    (result?.duplicate_lines || []).map(d => `${d.import_no}:${d.line_no}`),
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Find the raw ERP record for an order</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Shows the payload exactly as received — including records that imported
            as <strong>unchanged</strong> or <strong>skipped</strong>, which never
            appear on the Debug tab. Only the most recent pushes are retained.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            value={term}
            onChange={e => setTerm(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void search(); }}
            placeholder="Order number, e.g. SUB1030641"
            className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background
                       focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            onClick={() => void search()}
            disabled={loading || !term.trim()}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground
                       disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </button>
        </div>
      </div>

      {result && !result.found && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4">
          <div className="flex items-start gap-2">
            <Inbox className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold">
                {result.order_no} is not in the retained pushes
              </p>
              <p className="text-muted-foreground mt-1">
                The ERP has not sent this order in the pushes we still hold, so there
                is nothing here to apply. Either it has stopped being sent, or it
                arrived before the retention window.
              </p>
              {result.retained_pushes.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Retained: {result.retained_pushes.map(p => (
                    new Date(p.received_at).toLocaleString()
                  )).join(' · ')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {result && result.found && (
        <>
          {result.duplicate_lines.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold">
                    Sent more than once in the same push
                  </p>
                  <p className="text-muted-foreground mt-1">
                    {result.duplicate_lines.map(d => `line ${d.line_no}`).join(', ')} arrived
                    twice in one push. The last page processed wins, so the stored value
                    can be the older of the two — which looks exactly like the portal
                    ignoring a change.
                  </p>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            {result.match_count} record{result.match_count === 1 ? '' : 's'} for{' '}
            <strong>{result.order_no}</strong>
          </p>

          <div className="space-y-2">
            {result.matches.map((m: RawRecordMatch, i: number) => {
              const isDup = dupKeys.has(`${m.import_no}:${m.line_no}`);
              return (
                <div
                  key={`${m.import_no}-${m.page_index}-${m.row_index}`}
                  className={`rounded-lg border p-3 ${isDup ? 'border-red-300' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        OUTCOME_STYLES[m.outcome] || OUTCOME_STYLES['unchanged']
                      }`}>
                        {m.outcome}
                      </span>
                      <span className="font-medium">Line {m.line_no || '—'}</span>
                      {isDup && (
                        <span className="text-xs text-red-600 font-medium">duplicate</span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      page {m.page_index}/{m.page_count} · row {m.row_index} ·{' '}
                      {new Date(m.received_at).toLocaleString()}
                    </span>
                  </div>

                  <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3 mt-3">
                    {HIGHLIGHT.map(f => (
                      <div key={f.key} className="flex justify-between gap-2 text-xs">
                        <span className="text-muted-foreground">{f.label}</span>
                        <span className="font-mono">{fmt(m.record[f.key])}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    className="mt-3 text-xs text-primary hover:underline"
                  >
                    {expanded === i ? 'hide full payload' : 'view full payload'}
                  </button>
                  {expanded === i && (
                    <pre className="mt-2 p-3 rounded bg-muted text-xs overflow-x-auto">
                      {JSON.stringify(m.record, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
